import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to check user's launch status and handle routing guardrails.
 * 
 * Usage:
 * const { userStatus, loading, redirect } = useLaunchStatus();
 * 
 * Routing Rules:
 * - Founders visiting /pricing → redirect to /founder-upgrade
 * - Early Adopters visiting /pricing → redirect to /early-adopter-pricing
 * - Free users cannot access /founder-upgrade or /early-adopter-pricing
 */
export function useLaunchStatus() {
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLaunchStatus();
  }, []);

  const fetchLaunchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/launch/user-launch-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUserStatus(data);
        
        // Handle automatic redirects based on should_redirect_to
        if (data.should_redirect_to) {
          navigate(data.should_redirect_to);
        }
      }
    } catch (error) {
      console.error('Error fetching launch status:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if user can access a specific pricing page.
   * Returns { allowed: boolean, redirectTo: string | null }
   */
  const checkPricingAccess = (page) => {
    if (!userStatus) {
      return { allowed: true, redirectTo: null };
    }

    switch (page) {
      case 'founder-upgrade':
        // Only founders can access
        if (!userStatus.is_founder) {
          if (userStatus.is_early_adopter) {
            return { allowed: false, redirectTo: '/pricing/early-adopters' };
          }
          return { allowed: false, redirectTo: '/pricing' };
        }
        return { allowed: true, redirectTo: null };

      case 'early-adopter-pricing':
        // Only early adopters can access
        if (!userStatus.is_early_adopter) {
          if (userStatus.is_founder) {
            return { allowed: false, redirectTo: '/pricing/founders' };
          }
          return { allowed: false, redirectTo: '/pricing' };
        }
        return { allowed: true, redirectTo: null };

      case 'pricing':
        // Redirect founders and early adopters to their special pages
        if (userStatus.is_founder) {
          return { allowed: false, redirectTo: '/pricing/founders' };
        }
        if (userStatus.is_early_adopter && !userStatus.has_premium_access) {
          return { allowed: false, redirectTo: '/pricing/early-adopters' };
        }
        return { allowed: true, redirectTo: null };

      default:
        return { allowed: true, redirectTo: null };
    }
  };

  return {
    userStatus,
    loading,
    checkPricingAccess,
    isFounder: userStatus?.is_founder || false,
    isEarlyAdopter: userStatus?.is_early_adopter || false,
    hasPremiumAccess: userStatus?.has_premium_access || false,
    founderDaysRemaining: userStatus?.founder_days_remaining || 0,
    showCountdownBanner: userStatus?.show_countdown_banner || false,
    countdownMessage: userStatus?.countdown_message || null,
    countdownBannerType: userStatus?.countdown_banner_type || null,
    pricing: userStatus?.pricing || null,
    refresh: fetchLaunchStatus
  };
}

export default useLaunchStatus;
